import React from 'react'
import { Img, useCurrentFrame, useVideoConfig } from 'remotion'
import { useMediaAsset } from '../contexts'
import { useCroppableLayer } from '../crop'
import { useFadeOpacity } from '../fades'
import { ImageBackdrop } from '../fit'
import type { ImageItem } from '../types'

function ImageLayerUnmemoized({
  item,
  cropBackground,
}: {
  item: ImageItem
  cropBackground: boolean
}) {
  const frame = useCurrentFrame()
  const { fps, durationInFrames } = useVideoConfig()
  const asset = useMediaAsset(item.assetId)

  const opacity = useFadeOpacity({
    frame,
    fps,
    durationInFrames,
    fadeInDurationInSeconds: item.fadeInDurationInSeconds,
    fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
    opacity: item.opacity,
  })

  const { innerStyle, outerStyle } = useCroppableLayer({
    item,
    rotation: item.rotation,
    opacity,
    borderRadius: item.borderRadius,
    cropBackground,
    includeObjectFitInStyle: true,
  })

  if (!asset.remoteUrl) throw new Error(`Asset ${asset.id} has no URL`)

  // A tile's still of a fitted video (#547): the whole frame over its backdrop.
  const contain = item.fit === 'contain'
  return (
    <div style={outerStyle}>
      {contain ? (
        <ImageBackdrop backdrop={item.backdrop} src={asset.remoteUrl} />
      ) : null}
      <Img
        crossOrigin="anonymous"
        pauseWhenLoading
        style={contain ? { ...innerStyle, objectFit: 'contain' } : innerStyle}
        src={asset.remoteUrl}
      />
    </div>
  )
}

export const ImageLayer = React.memo(ImageLayerUnmemoized)
